import { Link } from "react-router-dom";
import { ArrowRight, Home as HomeIcon, Sun, MapPin, CalendarDays } from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";
import { useSiteContent } from "@/hooks/useSiteContent";

const SERVICES = [
  {
    icon: HomeIcon,
    titre: "Studio Quatre Saisons",
    texte: "Une séance confortable, peu importe la météo. Le décor d'automne est le premier décor lancé — les autres saisons suivront progressivement.",
    note: "Décor d'automne disponible à partir du 4 octobre, sur réservation. Éclairage par auto-projecteur pour des ambiances lumineuses uniques.",
  },
  {
    icon: Sun,
    titre: "À l'extérieur",
    texte: "Profitez des magnifiques couleurs et de la lumière dorée de l'automne. La Montérégie regorge de décors naturels spectaculaires.",
    note: "Idéal en fin de journée pour une lumière douce et cinématographique.",
  },
  {
    icon: MapPin,
    titre: "À l'endroit de votre choix",
    texte: "Votre propriété, un lieu significatif, un décor naturel ou toute autre destination qui vous tient à cœur.",
    note: "Aucun frais d'essence dans mon secteur et les environs.",
  },
];

export default function Services() {
  const content = useSiteContent();

  return (
    <main data-testid="services-page" className="pt-20">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <SectionHeading number="01" eyebrow="Services et séances" title="Trois façons de créer ensemble"
                        subtitle="En studio, à l'extérieur ou dans un lieu qui vous ressemble — chaque séance est pensée pour vous." />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {SERVICES.map((s, i) => (
            <Reveal key={s.titre} delay={i * 0.12}>
              <div className="gold-card p-10 h-full flex flex-col" data-testid={`service-card-${s.titre.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="w-14 h-14 border border-[#D4AF37]/40 rotate-45 flex items-center justify-center mb-8">
                  <s.icon size={22} className="text-[#D4AF37] -rotate-45" />
                </div>
                <h3 className="font-serif text-2xl text-[#F8F9FA]">{s.titre}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mt-4 flex-1">{s.texte}</p>
                <p className="text-[#D4AF37] text-xs leading-relaxed mt-6 border-t border-[#D4AF37]/20 pt-5">{s.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#0D0D10] border-y border-[#D4AF37]/10 py-24" data-testid="studio-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 border border-[#D4AF37]/25" />
              <img
                src="/images/Portrait d'un photographe professionnel Daniel.png"
                alt="Studio Quatre Saisons — ambiance"
                data-testid="studio-image"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </Reveal>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37]" data-testid="studio-eyebrow">02 — Le studio</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4" data-testid="studio-title">
              Studio <span className="gold-text italic">Quatre Saisons</span>
            </h2>
            <p className="text-slate-300 leading-loose mt-8 text-sm sm:text-base">
              Un espace confortable qui change au rythme des saisons. Le décor d'automne est le premier décor de
              lancement — chaleureux, doré et intemporel. L'éclairage par auto-projecteur permet de créer des ambiances
              lumineuses tout à fait uniques, peu importe la météo à l'extérieur.
            </p>
            <div className="flex items-center gap-3 mt-8 text-[#F3E5AB] text-sm" data-testid="studio-availability">
              <CalendarDays size={16} className="text-[#D4AF37]" />
              Disponible à partir du 4 octobre, sur réservation
            </div>
            <Link to="/contact" data-testid="studio-booking-button" className="btn-gold mt-10">
              Réserver une séance <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center" data-testid="services-cta">
        <Reveal>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Prêt à créer de beaux souvenirs ?</h2>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            <Link to="/tarifs" data-testid="services-pricing-button" className="btn-outline-gold">Voir les tarifs</Link>
            <Link to="/contact" data-testid="services-contact-button" className="btn-gold">Me contacter <ArrowRight size={15} /></Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
